// How the RFQ Center shows a row: its colours, its part line, and the four
// groupings (Inbox, RFQs, Parts, Suppliers) over the same rows.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/centerView.ts

import { CenterRow } from '../../../services/rfqCenterService';

export type CenterTab = 'inbox' | 'rfqs' | 'parts' | 'suppliers';
export type CenterFilter = 'all' | 'you' | 'wait' | 'stale' | 'quoted' | 'draft';

export const TAB_LABEL: Record<CenterTab, string> = { inbox: 'Inbox', rfqs: 'RFQs', parts: 'Parts', suppliers: 'Suppliers' };

/** Text and soft background per state, the same palette the RFQ rows always used. */
export function stateColors(row: Pick<CenterRow, 'state' | 'stale'>): { color: string; bg: string; rail: string } {
  if (row.stale) return { color: '#991B1B', bg: '#FEE2E2', rail: '#EF4444' };
  switch (row.state) {
    case 'you':
      return { color: '#1D4ED8', bg: '#DBEAFE', rail: '#3B82F6' };
    case 'wait':
      return { color: '#B45309', bg: '#FEF3C7', rail: '#F59E0B' };
    case 'quoted':
      return { color: '#047857', bg: '#D1FAE5', rail: '#10B981' };
    default:
      return { color: '#6B7280', bg: '#F3F4F6', rail: '#9CA3AF' };
  }
}

export function partRef(row: CenterRow): string {
  const first = row.lines[0];
  return first?.mpn || first?.partNumber || first?.description || 'Item';
}

export function askedLine(row: CenterRow): string {
  const first = row.lines[0];
  if (!first) return 'No lines';
  const qty = [first.quantity, first.unit].filter((v) => v !== undefined && v !== '').join(' ');
  const more = row.lines.length > 1 ? ` +${row.lines.length - 1}` : '';
  return `${qty ? `${qty} · ` : ''}${partRef(row)}${more}`;
}

export function quoteLine(row: CenterRow): string {
  const q = row.quote;
  if (!q || q.unitPrice === undefined) return 'None yet';
  const lead = q.leadTimeDays !== undefined ? ` · ${q.leadTimeDays} days` : '';
  return `${q.currency ?? ''} ${q.unitPrice}${lead}`.trim();
}

export function matchesFilter(row: CenterRow, filter: CenterFilter): boolean {
  if (row.state === 'closed') return false;
  if (filter === 'all') return true;
  if (filter === 'stale') return row.stale;
  return row.state === filter;
}

export function matchesSearch(row: CenterRow, identifier: string, text: string): boolean {
  const id = identifier.trim().toLowerCase();
  const tx = text.trim().toLowerCase();
  const ids = [row.rfqNumber, ...row.lines.flatMap((l) => [l.partNumber, l.mpn])].join(' ').toLowerCase();
  const words = [row.supplier.name, row.nowWith?.name, ...row.lines.flatMap((l) => [l.description, l.maker])].join(' ').toLowerCase();
  return (!id || ids.includes(id)) && (!tx || words.includes(tx));
}

/** Inbox order, like a mailbox: the newest activity on top. Drafts are not mail and stay out unless asked for. */
export function inboxRows(rows: CenterRow[], filter: CenterFilter): CenterRow[] {
  return rows
    .filter((r) => r.state !== 'draft' || filter === 'draft')
    .sort((a, b) => lastActivity(b) - lastActivity(a));
}

/** When something last happened on a conversation: the supplier's last message, else when we sent it. */
export function lastActivity(row: CenterRow): number {
  const at = row.latest?.when ?? row.sentAt;
  return at ? new Date(at).getTime() : 0;
}

/** That moment as a mail list shows it: the time today, the day this year, the full date before. */
export function activityLabel(row: CenterRow, now = new Date()): string {
  const at = lastActivity(row);
  if (!at) return '';
  const d = new Date(at);
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString(undefined, d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

/** One row of the RFQs, Parts or Suppliers view: several supplier conversations seen as one thing. */
export interface CenterGroup {
  key: string;
  /** The part (RFQs, Parts) or the supplier (Suppliers). */
  title: string;
  /** One quiet line under the title: the description, or what is open with the supplier. */
  subtitle: string;
  /** The row that decides the group's status: whatever needs the buyer first. */
  lead: CenterRow;
  /** Sent conversations first, what needs the buyer on top; drafts last. */
  rows: CenterRow[];
  counts: { asked: number; replied: number; quoted: number; drafts: number; you: number };
  /** The lowest quoted unit price in the group, as text. */
  bestQuote?: string;
  /** The latest thing a supplier wrote anywhere in the group. */
  latest?: string;
}

const leadOf = (rows: CenterRow[]): CenterRow => rows.find((r) => r.state === 'you') ?? rows.find((r) => r.stale) ?? rows.find((r) => r.state === 'wait') ?? rows.find((r) => r.state === 'quoted') ?? rows[0];
const ORDER: Record<CenterRow['state'], number> = { you: 0, wait: 1, quoted: 2, draft: 3, closed: 4 };
const byNeed = (a: CenterRow, b: CenterRow): number => ORDER[a.state] - ORDER[b.state] || Number(b.stale) - Number(a.stale) || (b.waitingOn.days ?? 0) - (a.waitingOn.days ?? 0);

/** Two supplier records with the same name are the same supplier to the buyer. */
const supplierKey = (r: CenterRow): string => r.supplier.name.trim().toLowerCase();

export function groupKey(row: CenterRow, tab: Exclude<CenterTab, 'inbox'>): string {
  if (tab === 'suppliers') return supplierKey(row);
  if (tab === 'parts') return partRef(row);
  return `${partRef(row)}|${row.lines[0]?.quantity ?? ''}|${row.bomId ?? ''}`;
}

export function groupRows(rows: CenterRow[], tab: Exclude<CenterTab, 'inbox'>): CenterGroup[] {
  const byKey = new Map<string, CenterRow[]>();
  for (const r of rows) byKey.set(groupKey(r, tab), [...(byKey.get(groupKey(r, tab)) ?? []), r]);
  const groups = Array.from(byKey.entries()).map(([key, members]): CenterGroup => {
    const rs = [...members].sort(byNeed);
    const a = rs[0];
    const first = a.lines[0];
    const quotes = rs.filter((r) => r.quote?.unitPrice !== undefined).sort((x, y) => (x.quote?.unitPrice ?? 0) - (y.quote?.unitPrice ?? 0));
    const newest = rs.filter((r) => r.latest).sort((x, y) => (y.latest?.when ?? '').localeCompare(x.latest?.when ?? ''))[0];
    const counts = {
      asked: rs.filter((r) => r.state !== 'draft').length,
      replied: rs.filter((r) => r.supplierMessages > 0).length,
      quoted: rs.filter((r) => r.quote).length,
      drafts: rs.filter((r) => r.state === 'draft').length,
      you: rs.filter((r) => r.state === 'you').length,
    };
    const qty = [first?.quantity, first?.unit].filter((v) => v !== undefined && v !== '').join(' ');
    const parts = new Set(rs.map(partRef)).size;
    return {
      key,
      title: tab === 'suppliers' ? a.supplier.name : partRef(a),
      subtitle: tab === 'suppliers' ? `${parts} ${parts === 1 ? 'part' : 'parts'}` : [tab === 'rfqs' ? qty : '', first?.description && first.description !== partRef(a) ? first.description : ''].filter(Boolean).join(' · '),
      lead: leadOf(rs),
      rows: rs,
      counts,
      bestQuote: quotes[0] ? quoteLine(quotes[0]) : undefined,
      latest: newest?.latest?.text,
    };
  });
  return groups.sort((a, b) => byNeed(a.lead, b.lead));
}

/** The neighbours the conversation offers: the Inbox list, or the other conversations of the same RFQ, part or supplier. */
export function siblingsOf(row: CenterRow, visible: CenterRow[], tab: CenterTab, filter: CenterFilter): CenterRow[] {
  if (tab === 'inbox') return inboxRows(visible, filter);
  return groupRows(visible, tab).find((g) => g.key === groupKey(row, tab))?.rows ?? [row];
}
